import { useFlowControlStore } from '@components/flow-controller'

import type { Schema } from '@tix/openapi-sdk'

import { getEnv } from '@/helpers/environment'
import { formatPickNOptionValue } from '@/helpers/flowStepValue'
import { type GA4CartItem, type GA4EventPayloadMap, triggerGA4Event } from '@/helpers/ga4'

type FlowDefinition = Schema<'flow_definition_min'>
type ActionPrompt = Schema<'action_prompt'>
type ActionInput = Schema<'action_input'>
type CorrelatedAction = {
  [K in ActionInput['type']]: {
    type: K
    input: Extract<ActionInput, { type: K }>
    prompt: Extract<ActionPrompt, { type: K }>
  }
}[ActionInput['type']]

/**
 * Side-effect-only composable: subscribes to the flow store's actions and emits
 * GA4 events. The host component does not need to import, call, or await any
 * tracking function — it just renders `<FlowController>` and navigates on
 * `@checkout`.
 *
 * Production lifecycle guarantees the subscription is set up before any action
 * runs: `useFlowGa4Tracking` is called synchronously in `FlowDetailsPage`'s
 * setup, while `FlowController` (which calls `store.initialize`) is mounted
 * inside `<Suspense>` in `FlowDetailsView` and only resolves after the parent
 * setup completes.
 */
export function useFlowGa4Tracking(flowDefinition: FlowDefinition) {
  const store = useFlowControlStore()

  store.$onAction(({ name, after }) => {
    switch (name) {
      case 'initialize':
        after((result) => {
          if (!result.success) return

          triggeFlowGA4Event('view_item', [
            {
              item_id: flowDefinition.id,
              item_name: flowDefinition.name,
              item_variant: 'bundle',
              location_id: flowDefinition.seller_id,
              quantity: 1,
              price: '0.00',
            },
          ])
        })
        return

      case 'submit': {
        // Skip `add_to_cart` when the user is submitting the `confirm` prompt.
        //
        // On the terminal `confirm → checkout` submit the store short-circuits and does NOT append,
        // so the tail still points at the previous step's input. Confirm isn't a useful step as
        // we immediately redirect to web-client checkout which fires a begin_checkout event.
        if (store.action?.type === 'confirm') return

        after((result) => {
          if (!result.success) return

          const newInput = store.previousInputs.at(-1)
          if (!newInput) return
          const action = pairInputWithPrompt(store.previousPrompts, newInput)
          if (!action) return
          const stepIndex = store.previousInputs.length - 1
          const items = actionToItems(action, flowDefinition, stepIndex)
          if (items.length === 0) return

          triggeFlowGA4Event('add_to_cart', items)
        })
        return
      }

      case 'goBackTo': {
        // Snapshot before the action runs and removes inputs and prompts from the store.
        const promptsBefore = store.previousPrompts.slice()
        const inputsBefore = store.previousInputs.slice()

        after((result) => {
          if (!result) return

          const removedFromIndex = store.previousInputs.length
          const removedInputs = inputsBefore.slice(removedFromIndex)

          const items: GA4CartItem[] = []
          for (let i = 0; i < removedInputs.length; i++) {
            const action = pairInputWithPrompt(promptsBefore, removedInputs[i])
            if (!action) continue
            items.push(...actionToItems(action, flowDefinition, removedFromIndex + i))
          }
          if (items.length === 0) return

          triggeFlowGA4Event('remove_from_cart', items)
        })
        return
      }
    }
  })
}

/**
 * Find the prompt matching the given input by `(resolver_id, type)` and return
 * them as a discriminated pair.
 *
 * @internal Exported only for unit tests in `useFlowGa4Tracking.spec.ts`. Do
 * not import from app code.
 *
 * @see https://github.com/stqry/tix-api/blob/691901d5ff4ca4708e0fc1ae1effb1f768a95a61/flows/engine/action.go#L28
 */
export function pairInputWithPrompt(prompts: ActionPrompt[], input: ActionInput): CorrelatedAction | undefined {
  const prompt = prompts.find((p) => p.resolver_id === input.resolver_id && p.type === input.type)
  if (!prompt) return undefined
  return { type: input.type, input, prompt } as CorrelatedAction
}

/**
 * Map a single correlated action to the GA4 cart items (add_to_cart/remove_from_cart) it represents.
 *
 * @internal Exported only for unit tests in `useFlowGa4Tracking.spec.ts`. Do
 * not import from app code.
 */
export function actionToItems(action: CorrelatedAction, flowDefinition: FlowDefinition, index: number): GA4CartItem[] {
  const base = {
    item_name: action.prompt.meta?.summary_title ?? '',
    item_id: action.prompt.resolver_id,
    item_list_id: flowDefinition.id,
    item_list_name: flowDefinition.name,
    location_id: flowDefinition.seller_id,
    price: '0.00',
    index,
  } as const

  switch (action.type) {
    case 'pick_n': {
      return (action.input.input.selected_resolver_ids ?? []).map((id) => ({
        ...base,
        item_variant: formatPickNOptionValue(id, action.prompt.options.options),
      }))
    }

    case 'pick_variants': {
      const counts = new Map<string, number>()
      for (const variant of action.input.input.variants ?? []) {
        counts.set(variant, (counts.get(variant) ?? 0) + 1)
      }
      return Array.from(counts, ([variant, quantity]) => ({
        ...base,
        item_variant: variant,
        quantity,
      }))
    }

    case 'pick_date': {
      const date = action.input.input.date!
      return [{ ...base, item_variant: date }]
    }

    case 'pick_time': {
      const id = action.input.input.id!
      return [{ ...base, item_variant: id }]
    }

    case 'survey':
    case 'message':
    case 'confirm':
      return [{ ...base, item_variant: action.type }]

    default: {
      // Compile-time exhaustiveness guard. If a new variant is added to
      // `ActionInput['type']` (e.g. via OpenAPI SDK regen) this becomes a
      // build error.
      action satisfies never
      return []
    }
  }
}

function triggeFlowGA4Event<E extends keyof GA4EventPayloadMap>(name: E, items: GA4EventPayloadMap[E]['items']): void {
  const currency = getEnv().portal.default_currency_code
  // Cast: TS can't statically verify the literal matches `GA4EventPayloadMap[E]`
  // for a generic `E` because the items type is distributed. The constraint on
  // `items` above still keeps the public API sound.
  triggerGA4Event(name, { currency, value: '0.00', items } as GA4EventPayloadMap[E])
}
